import IonIcon from '@reacticons/ionicons'
import cn from 'classnames'
import { useRouter } from 'next/router'
// import { features, title } from 'process'
import React, { Fragment, useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { CompatibleLink } from 'src/components/CompatibleLink/CompatibleLink'
import { IGetTopicsForHeaderQuery, useGetTopicsForHeaderQuery } from 'src/graphql/generated/hooks'
import { AppState } from 'src/store/types'
import { FeaturesType } from 'src/types'
import { removeTags } from 'src/utils/removeTags'

import { Avatar } from '../Avatar/Avatar'
import CartSubContent from '../Cart/CartSubContent'
import { HtmlContentViewer } from '../HtmlContentViewer/HtmlContentViewer'
import { PremiumModal } from '../PremiumModal'
import { LikeHiddenComponent } from './LikeHiddenComponent'

interface DottedDataListProps {
  title?: string
  isSecurity?: boolean
  isDataHostingLocations?: boolean
  isValueProposition?: boolean
  isServiceOfferings?: boolean
  features: FeaturesType[]
  colValue?: number
  isFounder?: boolean
  fromPage?: string
  parentGridCols?: number
  childGridCols?: number
  unlockPaywalledContent?: boolean
}

/**
 * Primary UI component for user interaction
 */
export const DottedDataList: React.FC<DottedDataListProps> = ({
  title,
  isSecurity,
  isDataHostingLocations,
  isValueProposition,
  isServiceOfferings,
  features = [],
  colValue,
  isFounder,
  fromPage = '',
  parentGridCols = 0,
  childGridCols = 0,
  unlockPaywalledContent = false,
}) => {
  const { isPremium } = useSelector((state: AppState) => state.authInfo)
  const canShowPaywalled = !!(isPremium || unlockPaywalledContent)
  const [isPremiumModalVisible, setIsPremiumModalVisible] = useState(false)

  const [isSecurityData, setIsSecurityData] = useState<boolean>(false)
  const [isDHLocationsData, setIsDHLocationsData] = useState<boolean>(false)
  const [isVPropositionData, setIsVPropositionData] = useState<boolean>(false)

  const router = useRouter()

  let topics: IGetTopicsForHeaderQuery | undefined = undefined

  const { data } = useGetTopicsForHeaderQuery({ fetchPolicy: 'cache-first' })

  const findTopic = (subTopicName: string) => {
    const find = topics?.topics.filter((item) => {
      const subTopics = item?.subTopics
      return subTopics?.some((subTopic) => subTopic?.name === subTopicName)
    })

    return find && find.length > 0
      ? `/topics/${find[0]?.slug}/${find[0]?.subTopics.filter((item) => item?.name === subTopicName)[0]?.slug}`
      : ''
  }
  // const randomNum = Math.floor(Math.random() * 1000) // Generates a number between 0 and 999

  useEffect(() => {
    if (isSecurity) setIsSecurityData(isSecurity)
    if (isDataHostingLocations) setIsDHLocationsData(isDataHostingLocations)
    if (isValueProposition) setIsVPropositionData(isValueProposition)
  }, [isSecurity, isDataHostingLocations, isValueProposition])

  useEffect(() => {
    if (isServiceOfferings) {
      topics = data
    }
  }, [isServiceOfferings])

  const parentGridColsClass = `grid grid-cols-${parentGridCols}`
  return (
    <>
      {parentGridCols > 0 && childGridCols > 0 ? (
        <div className={cn(`${colValue && { parentGridColsClass }} ${fromPage == 'compare' ? '' : 'mt-[13px]'} `)}>
          <ul className={cn('custom-bullet', `grid grid-cols-${childGridCols}`)}>
            {features.map((feature: any, index: number) => {
              return (
                <Fragment key={`DottedDataList-${index}`}>
                  <PremiumModal
                    origin={router.asPath}
                    isOpen={isPremiumModalVisible}
                    onClose={() => setIsPremiumModalVisible(false)}
                  />
                  {isServiceOfferings ? (
                    <CompatibleLink
                      href={findTopic(feature?.name)}
                      parentGridCols={parentGridCols}
                      childGridCols={childGridCols}
                    >
                      <CartSubContent
                        key={index}
                        type="list"
                        className={cn('mb-[0.125rem] ml-[24px]', {
                          '!text-base': isServiceOfferings,
                          '!text-base underline': findTopic(feature?.name),
                        })}
                      >
                        {feature?.name}
                      </CartSubContent>
                    </CompatibleLink>
                  ) : (
                    <Fragment>
                      {isSecurityData || isDHLocationsData || isVPropositionData ? (
                        <Fragment>
                          {canShowPaywalled ? (
                            <div
                              className={`flex flex-row ml-[24px] ${title === 'Value Proposition' ? 'value-proposition' : ''}`}
                            >
                              <IonIcon name="lock-open" className="mr-1 text-navy flex-shrink-0" />
                              {title === 'Value Proposition' ? (
                                <HtmlContentViewer html={feature?.name} />
                              ) : (
                                <div className="text-[15px] leading-[18px] tracking-normal font-primary">
                                  {feature?.name}
                                </div>
                              )}
                            </div>
                          ) : (
                            <LikeHiddenComponent title={title} fromPage="compare" />
                          )}
                        </Fragment>
                      ) : (
                        <>
                          {title &&
                          [
                            'Latest Funding Round',
                            'Total Funding Amount',
                            'Value Proposition',
                            'Security',
                            'Data Hosting Locations',
                          ].includes(title) ? (
                            <LikeHiddenComponent title={title} fromPage="compare" />
                          ) : (
                            <CartSubContent
                              key={index}
                              type={isFounder ? 'founder' : 'list'}
                              className={cn('mb-[0.125rem] ml-[24px]', {
                                'mb-[24px]': fromPage == 'compare' && isFounder,
                              })}
                            >
                              {!feature?.description ? (
                                <>
                                  {isFounder && (
                                    <Avatar
                                      size="27px"
                                      initialsTextSize="12px"
                                      url={feature.icon}
                                      name={feature?.name || feature?.title || feature?.company || ''}
                                      defaultMode="initials"
                                      border="circle"
                                      variant="navy"
                                    />
                                  )}
                                  {feature.link ? (
                                    <a href={feature.link} target="_blank" rel="noopener noreferrer">
                                      {[feature?.name, feature?.title, feature?.company]?.filter(Boolean).join(' - ')}
                                    </a>
                                  ) : (
                                    <label className="pl-2">
                                      {[feature?.name, feature?.title, feature?.company]?.filter(Boolean).join(' - ')}
                                    </label>
                                  )}
                                </>
                              ) : (
                                <div>
                                  <div className="flex mb-2">
                                    {isFounder &&
                                      (feature?.name || feature?.title || feature?.company || feature?.description) && (
                                        <Avatar
                                          size="27px"
                                          initialsTextSize="12px"
                                          url={feature?.icon}
                                          name={
                                            feature?.name ||
                                            feature?.title ||
                                            feature?.company ||
                                            removeTags(feature?.description || '') ||
                                            ''
                                          }
                                          defaultMode="initials"
                                          border="circle"
                                          variant="navy"
                                        />
                                      )}
                                    {feature.link ? (
                                      <a href={feature.link} target="_blank" rel="noopener noreferrer">
                                        {[feature?.name, feature?.title, feature?.company]?.filter(Boolean).join(' - ')}
                                      </a>
                                    ) : (
                                      <label className="pl-2">
                                        {[feature?.name, feature?.title, feature?.company]?.filter(Boolean).join(' - ')}
                                      </label>
                                    )}
                                  </div>
                                  <div className="value-proposition">
                                    <HtmlContentViewer
                                      className="body-des-html text-[15px] leading-[18px]"
                                      html={feature?.description || ''}
                                    />
                                  </div>
                                </div>
                              )}
                            </CartSubContent>
                          )}
                        </>
                      )}
                    </Fragment>
                  )}
                </Fragment>
              )
            })}
          </ul>
        </div>
      ) : (
        <div className={cn(`${colValue && 'grid grid-cols-2'} ${fromPage == 'compare' ? '' : 'mt-[13px]'} `)}>
          {features.map((feature: any, index: number) => {
            return (
              <Fragment key={`DottedDataList-${index}`}>
                {/* <PremiumModal
                  origin={router.asPath}
                  isOpen={isPremiumModalVisible}
                  onClose={() => setIsPremiumModalVisible(false)}
                /> */}
                {isServiceOfferings ? (
                  <CompatibleLink href={findTopic(feature?.name)}>
                    <CartSubContent
                      key={index}
                      type="list"
                      className={cn('mb-[0.125rem] ml-[24px]', {
                        '!text-base': isServiceOfferings,
                        '!text-base underline': findTopic(feature?.name),
                      })}
                    >
                      {feature?.name}
                    </CartSubContent>
                  </CompatibleLink>
                ) : (
                  <Fragment>
                    {isSecurityData || isDHLocationsData || isVPropositionData ? (
                      <Fragment>
                        {canShowPaywalled ? (
                          <div
                            className={`flex flex-row ml-[24px] ${title === 'Value Proposition' ? 'value-proposition' : ''}`}
                          >
                            <IonIcon name="lock-open" className="mr-1 text-navy flex-shrink-0" />
                            {title === 'Value Proposition' ? (
                              <HtmlContentViewer html={feature?.name} />
                            ) : (
                              <div className="text-[15px] leading-[18px] tracking-normal font-primary">
                                {feature?.name}
                              </div>
                            )}
                          </div>
                        ) : (
                          <LikeHiddenComponent title={title} fromPage="compare" />
                        )}
                      </Fragment>
                    ) : (
                      <>
                        {title &&
                        [
                          'Latest Funding Round',
                          'Total Funding Amount',
                          'Value Proposition',
                          'Security',
                          'Data Hosting Locations',
                        ].includes(title) ? (
                          <LikeHiddenComponent title={title} fromPage="compare" />
                        ) : (
                          <CartSubContent
                            key={index}
                            type={isFounder ? 'founder' : 'list'}
                            className={cn('mb-[0.125rem] ml-[24px]', {
                              'mb-[24px]': fromPage == 'compare' && isFounder,
                            })}
                          >
                            {!feature?.description ? (
                              <>
                                {isFounder && (
                                  <Avatar
                                    size="27px"
                                    initialsTextSize="12px"
                                    url={feature.icon}
                                    name={feature?.name || feature?.title || feature?.company || ''}
                                    defaultMode="initials"
                                    border="circle"
                                    variant="navy"
                                  />
                                )}
                                {feature.link ? (
                                  <a href={feature.link} target="_blank" rel="noopener noreferrer">
                                    {[feature?.name, feature?.title, feature?.company]?.filter(Boolean).join(' - ')}
                                  </a>
                                ) : (
                                  <label className="pl-2">
                                    {[feature?.name, feature?.title, feature?.company]?.filter(Boolean).join(' - ')}
                                  </label>
                                )}
                              </>
                            ) : (
                              <div>
                                <div className="flex mb-2">
                                  {isFounder &&
                                    (feature?.name || feature?.title || feature?.company || feature?.description) && (
                                      <Avatar
                                        size="27px"
                                        initialsTextSize="12px"
                                        url={feature?.icon}
                                        name={
                                          feature?.name ||
                                          feature?.title ||
                                          feature?.company ||
                                          removeTags(feature?.description || '') ||
                                          ''
                                        }
                                        defaultMode="initials"
                                        border="circle"
                                        variant="navy"
                                      />
                                    )}
                                  {feature.link ? (
                                    <a href={feature.link} target="_blank" rel="noopener noreferrer">
                                      {[feature?.name, feature?.title, feature?.company]?.filter(Boolean).join(' - ')}
                                    </a>
                                  ) : (
                                    <label className="pl-2">
                                      {[feature?.name, feature?.title, feature?.company]?.filter(Boolean).join(' - ')}
                                    </label>
                                  )}
                                </div>
                                <div className="value-proposition">
                                  <HtmlContentViewer
                                    className="body-des-html text-[15px] leading-[18px]"
                                    html={feature?.description || ''}
                                  />
                                </div>
                              </div>
                            )}
                          </CartSubContent>
                        )}
                      </>
                    )}
                  </Fragment>
                )}
              </Fragment>
            )
          })}
        </div>
      )}
    </>
  )
}
